// Copyright (c) Ping. All rights reserved.
//
// Recovery-backup V3 — the mnemonic-based recovery envelope shared
// byte-for-byte with iOS. Replaces the old app-level `RecoveryBundleV2`
// (PBKDF2 + Crockford passphrase + conversation snapshots) with:
//
//   (a) a 12-word BIP39 mnemonic the user writes down,
//   (b) the SDK's Argon2id `encryptBackup`/`decryptBackup` for the SDK
//       identity export + DeviceGroup snapshot, and
//   (c) a SEPARATE HKDF-wrapped companion that carries the auth-layer
//       `__user_identity_key` (an Ed25519 key distinct from the SDK
//       identity). The SDK's IdentityBackup has no slot for it, so
//       restoring via the SDK alone would present a fresh auth pubkey →
//       `/v1/auth/verify` 403 `identity_mismatch`. We back it up
//       ourselves under a key derived from the SDK seed (which is itself
//       Argon2id-protected by the mnemonic).
//
// Conversation snapshots are DROPPED entirely (forward secrecy: no
// per-conversation epoch secrets in the blob). `conversationSnapshots`
// is always `[]`.
//
// ============================================================================
// EXACT CRYPTO ENVELOPE (must match iOS byte-for-byte)
// ============================================================================
//
// The string uploaded as `ciphertext` to POST /v1/recovery/blobs is
// UTF-8 JSON:
//
//   RecoveryBlobV3 = {
//     "v": 3,
//     "sdk":  <base64url-no-pad of sdkBlob>,
//     "auth": <base64url-no-pad of authCombined>
//   }
//
// where:
//
//   sdkBlob = MessagingClient.encryptBackup({
//     mnemonicPhrase,
//     accountId,                 // 16 bytes
//     identityExport: S,         // raw SDK identity export bytes
//     deviceGroupSnapshot,       // DeviceGroup state snapshot, or 0 bytes
//     conversationSnapshots: [], // ALWAYS empty (FS-safe)
//     createdAtMs: Date.now(),
//   })
//
//   authCombined = nonce(12) || AES-256-GCM-ciphertext || tag(16)
//     (WebCrypto `subtle.encrypt` appends the 16-byte tag to the
//      ciphertext, matching iOS `AES.GCM.SealedBox.combined`.)
//
//   wrapKey = HKDF-SHA256(
//     IKM  = S,
//     salt = <empty>,
//     info = UTF8("ping/recovery/auth-key-wrap/v1"),
//     L    = 32 bytes,
//   ) imported as an AES-GCM-256 key.
//
//   authPlain = UTF8(JSON.stringify(await exportUserIdentityForBackup()))
//     // { jwk, publicKeyBytes } — cross-platform with iOS
//     //    AuthUserIdentityBackup.

import { MessagingClient } from 'ping-openmls-sdk';

import {
  // Canonical base64url (no pad) helpers — reused so V3's `sdk`/`auth`
  // fields encode identically to the rest of the recovery surface.
  bytesToUrlB64NoPad,
  urlB64NoPadToBytes,
} from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';
import { identityManager } from '@ping/ui/src/platform/identity.web';

import { log } from '@core/log/log';
import {
  exportUserIdentityForBackup,
  restoreUserIdentityFromBackup,
} from '@core/network/deviceKeys';
import { retrieveIdentity } from '@core/persistence/identity';

import { stashPendingDeviceGroupSnapshot } from './recoveryRestorePending';

/** HKDF `info` label for deriving the auth-companion AES-GCM key. MUST match iOS. */
const AUTH_KEY_WRAP_INFO = 'ping/recovery/auth-key-wrap/v1';

/** Wire envelope. `sdk`/`auth` are base64url-no-pad. */
interface RecoveryBlobV3 {
  v: 3;
  sdk: string;
  auth: string;
}

/** Auth-companion plaintext WIRE shape. Cross-platform with iOS, which encodes
 *  `AuthUserIdentityBackup` with `public_key_bytes` (snake_case) — so a backup
 *  made on iOS restores on web and vice-versa. (NOTE: `exportUserIdentityForBackup`
 *  returns the camelCase `publicKeyBytes` internally; we translate at the wire
 *  boundary here so the JSON byte-matches iOS's `JSONEncoder` output.) */
interface AuthUserIdentityWire {
  jwk: JsonWebKey;
  public_key_bytes: number[];
}

/**
 * Thrown when a fetched recovery blob is not a V3 envelope (e.g. an old
 * v2 JSON bundle or legacy raw SDK-identity bytes). The restore host
 * surfaces this BEFORE consuming OTPs so the user gets a clear "older
 * app version" message instead of a downstream 403.
 */
export class LegacyRecoveryBlobError extends Error {
  constructor(message?: string) {
    super(
      message ??
        "This backup was created with an older app version and can't be restored — please sign up fresh.",
    );
    this.name = 'LegacyRecoveryBlobError';
  }
}

// ── SDK client subset for the DeviceGroup snapshot export ────────────

interface RecoverySnapshotClient {
  listConversations(): Promise<Array<{ id: Uint8Array; is_device_group: boolean }>>;
  exportConversationStateSnapshot(id: Uint8Array): Promise<Uint8Array>;
}

function liveClient(): RecoverySnapshotClient | null {
  const session = useSessionStore.getState();
  if (session.status.kind !== 'authenticated') return null;
  return session.status.client as unknown as RecoverySnapshotClient;
}

/**
 * Export the DeviceGroup's MLS state snapshot if a live SDK client is
 * available. Returns `new Uint8Array(0)` when there's no live client or
 * no DeviceGroup conversation. Best-effort: a failed export degrades to
 * an empty snapshot rather than blocking the backup.
 */
async function exportDeviceGroupSnapshot(): Promise<Uint8Array> {
  const client = liveClient();
  if (!client) return new Uint8Array(0);
  try {
    const metas = await client.listConversations();
    const dg = metas.find((m) => m.is_device_group === true);
    if (!dg) return new Uint8Array(0);
    return await client.exportConversationStateSnapshot(dg.id);
  } catch (err) {
    log.warn({
      event: 'recovery.v3.deviceGroupSnapshotFailed',
      message: (err as Error).message,
    });
    return new Uint8Array(0);
  }
}

// ── HKDF auth-companion key ──────────────────────────────────────────

/**
 * Derive the AES-GCM-256 wrap key from the SDK identity-export bytes `S`
 * via HKDF-SHA256 (empty salt, fixed `info`). Both encrypt + decrypt
 * import the same handle so the companion round-trips.
 */
async function deriveAuthWrapKey(
  s: Uint8Array,
  usages: KeyUsage[],
): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey(
    'raw',
    s as BufferSource,
    'HKDF',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: new TextEncoder().encode(AUTH_KEY_WRAP_INFO),
    },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    usages,
  );
}

function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

// ── build (backup) ───────────────────────────────────────────────────

/**
 * Build the V3 recovery-blob JSON string to upload as `ciphertext`.
 *
 * Does the SDK Argon2id `encryptBackup` (SDK identity + DeviceGroup
 * snapshot, no conversation snapshots) + the HKDF-wrapped auth-layer
 * companion, then packs both into `RecoveryBlobV3`.
 */
export async function buildRecoveryBlobV3(mnemonicPhrase: string): Promise<string> {
  // S = the raw SDK identity export bytes held in memory by the identity
  // manager (unwrapped during bootstrap). Used BOTH as the SDK
  // `identityExport` and as the HKDF IKM for the auth companion.
  const s = await identityManager.unwrapIdentity();
  if (!s) {
    // Boot order is wrong — we're rendering before bootstrap unwrapped
    // the identity. Surface a friendly retryable message.
    throw new Error('Identity is still loading. Try again in a moment.');
  }

  // accountId = 16 bytes decoded from the persisted base64url account id.
  const persisted = await retrieveIdentity();
  if (!persisted || persisted.accountId.length === 0) {
    throw new Error('Account id is unavailable. Try again in a moment.');
  }
  const accountId = urlB64NoPadToBytes(persisted.accountId);

  const deviceGroupSnapshot = await exportDeviceGroupSnapshot();

  // (a)+(b) SDK Argon2id backup of identity + DeviceGroup. Conversation
  // snapshots are ALWAYS empty (forward-secrecy safe).
  const sdkBlob = await MessagingClient.encryptBackup({
    mnemonicPhrase,
    accountId,
    identityExport: s,
    deviceGroupSnapshot,
    conversationSnapshots: [],
    createdAtMs: Date.now(),
  });

  // (c) HKDF-wrapped auth-layer companion. Serialize to the snake_case WIRE
  // shape so the JSON byte-matches iOS's `JSONEncoder(AuthUserIdentityBackup)`
  // (`public_key_bytes`), enabling cross-platform restore.
  const exported = await exportUserIdentityForBackup();
  const authWire: AuthUserIdentityWire = {
    jwk: exported.jwk,
    public_key_bytes: exported.publicKeyBytes,
  };
  const authPlain = new TextEncoder().encode(JSON.stringify(authWire));
  const wrapKey = await deriveAuthWrapKey(s, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    wrapKey,
    authPlain as BufferSource,
  );
  // authCombined = nonce || ciphertext || tag (WebCrypto appends tag to ct).
  const authCombined = concatBytes(iv, new Uint8Array(ct));

  const blob: RecoveryBlobV3 = {
    v: 3,
    sdk: bytesToUrlB64NoPad(sdkBlob),
    auth: bytesToUrlB64NoPad(authCombined),
  };
  // The `POST /v1/recovery/blobs` endpoint stores `ciphertext` as an OPAQUE
  // base64url blob (it base64url-decodes + persists the bytes, server-blind).
  // So base64url-encode the envelope JSON — sending the raw JSON string fails
  // the server's base64url decode with 400 invalid_request.
  return bytesToUrlB64NoPad(new TextEncoder().encode(JSON.stringify(blob)));
}

// ── restore ──────────────────────────────────────────────────────────

/** Parse + validate the V3 envelope, or throw `LegacyRecoveryBlobError`. */
function parseRecoveryBlobV3(ciphertext: string): RecoveryBlobV3 {
  let parsed: unknown;
  try {
    // `ciphertext` is base64url(JSON envelope) — mirror `buildRecoveryBlobV3`.
    // A legacy (v1/v2 PBKDF2) blob base64url-decodes to AES-GCM bytes that
    // aren't JSON, so the parse throws → LegacyRecoveryBlobError (fast-fail).
    parsed = JSON.parse(new TextDecoder().decode(urlB64NoPadToBytes(ciphertext)));
  } catch {
    throw new LegacyRecoveryBlobError();
  }
  if (
    parsed === null ||
    typeof parsed !== 'object' ||
    (parsed as { v?: unknown }).v !== 3 ||
    typeof (parsed as { sdk?: unknown }).sdk !== 'string' ||
    typeof (parsed as { auth?: unknown }).auth !== 'string'
  ) {
    throw new LegacyRecoveryBlobError();
  }
  return parsed as RecoveryBlobV3;
}

/**
 * Restore identity from a V3 recovery blob.
 *
 *   1. Parse + validate the envelope (throws `LegacyRecoveryBlobError`
 *      on anything that isn't v3).
 *   2. `decryptBackup(sdkBlob, mnemonic)` → SDK identity export `S`.
 *   3. Install the SDK identity via `identityManager.wrapIdentity(S)`.
 *   4. If a DeviceGroup snapshot is present, stash it for post-bootstrap
 *      import (no live client exists yet at restore time — App.tsx
 *      imports it once the session is authenticated).
 *   5. HKDF(S) → AES-GCM-decrypt the auth companion → restore the
 *      auth-layer user identity key.
 */
export async function restoreFromRecoveryBlobV3(
  ciphertext: string,
  mnemonicPhrase: string,
): Promise<void> {
  const blob = parseRecoveryBlobV3(ciphertext);

  const sdkBlobBytes = urlB64NoPadToBytes(blob.sdk);
  const backup = await MessagingClient.decryptBackup(sdkBlobBytes, mnemonicPhrase);
  const s = backup.identity_export;

  // Install the SDK identity exactly like the legacy restore path did.
  await identityManager.wrapIdentity(s);

  // The DeviceGroup snapshot can only be imported into a live SDK client,
  // which doesn't exist yet (bootstrap runs after restore). Stash it for
  // the post-bootstrap one-shot importer (mirrors the old conversation-
  // snapshot hand-off).
  if (backup.device_group_snapshot.length > 0) {
    stashPendingDeviceGroupSnapshot(backup.device_group_snapshot);
  }

  // Decrypt + restore the auth-layer user identity companion.
  const authCombined = urlB64NoPadToBytes(blob.auth);
  if (authCombined.length < 12 + 16) {
    throw new Error('Recovery blob auth companion is malformed.');
  }
  const iv = authCombined.subarray(0, 12);
  const ctWithTag = authCombined.subarray(12);
  const wrapKey = await deriveAuthWrapKey(s, ['decrypt']);
  const authPlainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    wrapKey,
    ctWithTag as BufferSource,
  );
  const parsedAuth = JSON.parse(
    new TextDecoder().decode(authPlainBuf),
  ) as AuthUserIdentityWire;
  await restoreUserIdentityFromBackup({
    jwk: parsedAuth.jwk,
    publicKeyBytes: parsedAuth.public_key_bytes,
  });

  log.info({
    event: 'recovery.v3.restored',
    hasDeviceGroupSnapshot: backup.device_group_snapshot.length > 0,
  });
}
